/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Item Schedules
 * Feature #183: External Metadata Enrichment
 * Constitution: v1.18.0
 * Conformed to Spec 197 design blueprint
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { Clock, CalendarClock } from 'lucide-react';
import { useItemSchedules } from '../../services/operations/metadataService';
import {
  PageLayout,
  Card,
  DataTable,
  Badge,
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import { formatEastern } from '@/utils/dateFormatter';

type ScheduleRow = NonNullable<ReturnType<typeof useItemSchedules>['data']>['schedules'][number];

function formatNextRun(utc: string | null): string {
  if (!utc) return '—';
  return formatEastern(utc);
}

export const ItemSchedulesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, refetch } = useItemSchedules(id ?? null);

  const columns: DataTableColumn<ScheduleRow>[] = [
    {
      key: 'scheduleType',
      header: 'Schedule Type',
      width: '22%',
      render: (s) => (
        <span className="flex items-center gap-1.5 font-medium text-[var(--color-text-primary)]">
          <Clock className="h-4 w-4 flex-shrink-0 text-[var(--color-text-secondary)]" />
          {s.scheduleType}
        </span>
      ),
    },
    {
      key: 'isEnabled',
      header: 'Enabled',
      width: '14%',
      render: (s) =>
        s.isEnabled ? (
          <Badge variant="success" isOutlined>
            Enabled
          </Badge>
        ) : (
          <Badge variant="secondary" isOutlined>
            Disabled
          </Badge>
        ),
    },
    {
      key: 'configuration',
      header: 'Configuration',
      width: '44%',
      render: (s) =>
        s.configuration ? (
          <pre className="max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded bg-[var(--color-bg-subtle)] px-2 py-1 text-xs text-[var(--color-text-primary)]">
            <code>{JSON.stringify(s.configuration, null, 2)}</code>
          </pre>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      key: 'nextRunUtc',
      header: 'Next Run',
      width: '20%',
      render: (s) => (
        <span className="text-[var(--color-text-secondary)]">{formatNextRun(s.nextRunUtc)}</span>
      ),
    },
  ];

  if (isLoading) {
    return (
      <PageLayout
      helpId="auto.item-schedules" title="Item Schedules" icon={<CalendarClock className="h-6 w-6" />}>
        <Card className="p-4">
          <LoadingState rows={6} />
        </Card>
      </PageLayout>
    );
  }

  if (isError) {
    return (
      <PageLayout title="Item Schedules" icon={<CalendarClock className="h-6 w-6" />}>
        <ErrorState
          title="Failed to load schedules"
          message="The item schedules could not be loaded."
          onRetry={() => void refetch()}
        />
      </PageLayout>
    );
  }

  if (!data) {
    return (
      <PageLayout title="Item Schedules" icon={<CalendarClock className="h-6 w-6" />}>
        <Card className="p-4">
          <EmptyState
            title="Item not found"
            message="This Fabric item does not exist or has not been collected."
          />
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={data.itemName}
      description={`${data.schedules.length} schedule${data.schedules.length !== 1 ? 's' : ''} configured`}
      icon={<CalendarClock className="h-6 w-6" />}
      actions={
        <Badge variant="info" isOutlined>
          {data.itemType}
        </Badge>
      }
    >
      {data.schedules.length === 0 ? (
        <Card className="p-4">
          <EmptyState
            title="No schedules configured"
            message="This item has no job schedules configured, or schedules have not been collected yet."
          />
        </Card>
      ) : (
        <Card padding="none">
          <DataTable
            columns={columns}
            rows={data.schedules}
            rowKey={(s) => s.id}
            emptyMessage="No schedules configured"
            minWidth="44rem"
          />
        </Card>
      )}
    </PageLayout>
  );
};

export default ItemSchedulesPage;
